import { Link } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Bell } from 'lucide-react'
import type { ActivityEvent, ActivityType, Notification } from '@ousse/shared'
import { api } from '../lib/api'
import { activityLink, formatActivityMessage, formatRelativeTime } from '../lib/activity'
import { ErrorAlert } from '../components/ui'

function asActivityEvent(n: Notification): ActivityEvent {
  const previewType = (typeof n.data.previewType === 'string' ? n.data.previewType : 'item.updated') as ActivityType
  const previewData =
    n.data.previewData && typeof n.data.previewData === 'object'
      ? (n.data.previewData as Record<string, unknown>)
      : {}
  return {
    id: n.eventIds[0] ?? n.id,
    type: previewType,
    source: 'web',
    userId: typeof n.data.actorId === 'string' ? n.data.actorId : null,
    displayName: typeof n.data.actorName === 'string' ? n.data.actorName : null,
    entityType: typeof n.data.entityType === 'string' ? n.data.entityType : null,
    entityId: typeof n.data.entityId === 'string' ? n.data.entityId : null,
    data: previewData,
    createdAt: n.createdAt,
  }
}

function notificationHref(n: Notification): string | null {
  if (typeof n.data.link === 'string' && n.data.link.startsWith('/')) return n.data.link
  return activityLink(asActivityEvent(n))
}

/** Centre de notifications (cloche) : toutes les foyers de l’utilisateur. */
export function NotificationsPage() {
  const { t, i18n } = useTranslation()
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: ['notifications'],
    queryFn: () =>
      api.get<{ items: Notification[]; unreadCount: number; nextCursor: string | null }>(
        '/notifications?limit=50',
      ),
  })

  const markAll = useMutation({
    mutationFn: () => api.post('/notifications/read-all', {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const markOne = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const items = query.data?.items ?? []
  const unread = query.data?.unreadCount ?? 0

  return (
    <>
      <h1>
        <span className="row" style={{ gap: '0.5rem' }}>
          <Bell aria-hidden size={28} />
          {t('notifications.title')}
        </span>
      </h1>
      {unread > 0 && (
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <p className="muted">{t('notifications.unreadCount', { count: unread })}</p>
          <button type="button" className="btn secondary" onClick={() => markAll.mutate()} disabled={markAll.isPending}>
            {t('notifications.markAllRead')}
          </button>
        </div>
      )}
      <ErrorAlert error={query.error} />
      {query.isPending && <p role="status">{t('common.loading')}</p>}
      {!query.isPending && items.length === 0 && <p className="muted">{t('notifications.empty')}</p>}
      {items.length > 0 && (
        <ul className="list">
          {items.map((n) => {
            const event = asActivityEvent(n)
            const count = typeof n.data.count === 'number' ? n.data.count : 1
            const user = event.displayName?.trim() || t('activity.someone')
            const text =
              count > 1
                ? t('notifications.grouped', { user, count })
                : formatActivityMessage(t, event)
            const href = notificationHref(n)
            const when = formatRelativeTime(i18n.language, n.createdAt)
            const unreadItem = !n.readAt
            const body = (
              <div className="stack" style={{ flex: 1 }}>
                <strong style={{ fontWeight: unreadItem ? 700 : 500 }}>{text}</strong>
                <span className="muted">
                  {t(`notifications.categories.${n.type}`)} · {when}
                </span>
              </div>
            )
            return (
              <li key={n.id}>
                {href ? (
                  <Link
                    to={href}
                    className="row"
                    style={{ textDecoration: 'none', color: 'inherit', flex: 1, width: '100%' }}
                    onClick={() => {
                      if (unreadItem) markOne.mutate(n.id)
                    }}
                  >
                    {body}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="row"
                    style={{
                      flex: 1,
                      width: '100%',
                      textAlign: 'left',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: unreadItem ? 'pointer' : 'default',
                    }}
                    onClick={() => {
                      if (unreadItem) markOne.mutate(n.id)
                    }}
                  >
                    {body}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}
